import { useEffect, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Columns3,
  MoreVertical,
  Eye,
  RotateCcw,
  Trash2,
  XCircle,
} from "lucide-react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TaskPriorityBadge } from "./TaskPriorityBadge";
import type { TaskCancelMode } from "./TaskCancelDialog";
import { getSubtaskProgress, type WorkspaceTask } from "@/hooks/useWorkspaceTasks";
import { getTaskUrgency } from "@/lib/task-urgency";
import { cn } from "@/lib/utils";

interface KanbanSubtask {
  id: string;
  status: WorkspaceTask["status"];
  title?: string;
}

interface TasksKanbanCardProps {
  task: WorkspaceTask;
  isAdmin: boolean;
  currentMemberId: string | null;
  onComplete: (task: WorkspaceTask) => void;
  onReopen: (taskId: string) => void;
  onView: (task: WorkspaceTask) => void;
  onCancelRequest?: (task: WorkspaceTask) => void;
  dragDisabled?: boolean;
  moveColumns?: { id: string; name: string; color: string }[];
  onMoveToColumn?: (task: WorkspaceTask, columnId: string) => void;
}

function cancelModeFor(
  task: WorkspaceTask,
  isAdmin: boolean,
  currentMemberId: string | null,
): TaskCancelMode {
  if (isAdmin) return "cancel";
  if (task.created_by_member_id === currentMemberId) return "cancel";
  return "request";
}

export function TasksKanbanCard({
  task,
  isAdmin,
  currentMemberId,
  onComplete,
  onReopen,
  onView,
  onCancelRequest,
  dragDisabled,
  moveColumns = [],
  onMoveToColumn,
}: TasksKanbanCardProps) {
  const [subtasksExpanded, setSubtasksExpanded] = useState(false);
  const isCompleted = task.status === "completed";
  const isCancelled = task.status === "cancelled";
  const canEdit = isAdmin || task.assigned_member_id === currentMemberId;
  const cancelMode = cancelModeFor(task, isAdmin, currentMemberId);
  const subProgress = getSubtaskProgress(task);
  const urgency = getTaskUrgency({
    status: task.status,
    due_date: task.due_date,
    due_at: task.due_at,
  });
  const subtasks = (task.subtasks ?? []) as KanbanSubtask[];
  const skipClickRef = useRef(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({ id: task.id, disabled: Boolean(dragDisabled) });

  useEffect(() => {
    if (isDragging) skipClickRef.current = true;
  }, [isDragging]);

  const handleCardClick = () => {
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    onView(task);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? undefined : transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {isOver && (
        <div className="h-0.5 bg-primary rounded-full mx-1 mb-1 animate-in fade-in duration-200" />
      )}
      <Card
        {...attributes}
        {...(!dragDisabled ? listeners : {})}
        className={cn(
          "p-4 md:p-3 rounded-2xl md:rounded-lg shadow-sm transition-opacity",
          !dragDisabled && "cursor-grab active:cursor-grabbing hover:bg-muted/30",
          isCompleted && "opacity-60",
          isDragging && "opacity-30",
          urgency.cardClassName,
        )}
        onClick={handleCardClick}
      >
        <div className="flex items-start gap-2">
          <Checkbox
            checked={isCompleted}
            disabled={!canEdit}
            onCheckedChange={() => {
              if (isCompleted) onReopen(task.id);
              else onComplete(task);
            }}
            className="mt-0.5"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />

          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-start gap-1">
              <span
                className={cn(
                  "flex-1 min-w-0 text-sm font-medium leading-tight line-clamp-2",
                  isCompleted && "line-through text-muted-foreground",
                )}
              >
                {task.title}
              </span>
              {canEdit && !isCancelled && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 md:h-6 md:w-6 shrink-0"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onView(task)}>
                      <Eye className="h-4 w-4 mr-2" />
                      Visualizar
                    </DropdownMenuItem>
                    {isCompleted && (
                      <DropdownMenuItem onClick={() => onReopen(task.id)}>
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Reabrir
                      </DropdownMenuItem>
                    )}
                    {onMoveToColumn && moveColumns.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="flex items-center gap-2 font-normal text-muted-foreground">
                          <Columns3 className="h-3.5 w-3.5" />
                          Mover para
                        </DropdownMenuLabel>
                        {moveColumns.map((column) => (
                          <DropdownMenuItem
                            key={column.id}
                            onClick={() => onMoveToColumn(task, column.id)}
                          >
                            <span className={cn("h-2 w-2 rounded-full mr-2 shrink-0", column.color)} />
                            <span className="truncate">{column.name}</span>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    {onCancelRequest && (
                      <DropdownMenuItem
                        onClick={() => onCancelRequest(task)}
                        className="text-destructive focus:text-destructive"
                      >
                        {cancelMode === "request" ? (
                          <>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Solicitar exclusão
                          </>
                        ) : (
                          <>
                            <XCircle className="h-4 w-4 mr-2" />
                            Cancelar
                          </>
                        )}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-1">
              <TaskPriorityBadge
                priority={task.priority}
                className="h-5 text-[10px] rounded-full border-0 px-2"
              />
              {urgency.label && (
                <Badge
                  variant="outline"
                  className={cn("text-[10px] h-5 rounded-full border-0", urgency.badgeClassName)}
                >
                  {urgency.label}
                </Badge>
              )}
              <span
                className={cn(
                  "inline-flex items-center gap-1 h-5 rounded-full px-2 text-[10px] font-medium",
                  task.due_date
                    ? "bg-teal-500/10 text-teal-800 dark:text-teal-300"
                    : "bg-muted text-muted-foreground",
                )}
              >
                <Clock className="h-3 w-3" />
                {task.due_date
                  ? format(parseISO(task.due_date), "dd MMM", { locale: ptBR })
                  : "Sem data"}
              </span>
              {isAdmin && task.assignee?.display_name && (
                <span className="inline-flex items-center h-5 max-w-full rounded-full px-2 text-[10px] font-medium bg-primary/10 text-primary truncate">
                  {task.assignee.display_name}
                </span>
              )}
            </div>

            {subProgress.total > 0 && (
              <div className="space-y-1.5">
                <button
                  type="button"
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground min-h-8"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSubtasksExpanded((v) => !v);
                  }}
                >
                  {subtasksExpanded ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  {subProgress.done}/{subProgress.total} etapas
                </button>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-[width] duration-300"
                    style={{ width: `${Math.min(100, (subProgress.done / subProgress.total) * 100)}%` }}
                  />
                </div>
              </div>
            )}

            {subtasksExpanded && subtasks.length > 0 && (
              <ul className="mt-1 space-y-0.5 border-l-2 border-muted pl-2">
                {subtasks.map((st) => (
                  <li
                    key={st.id}
                    className={cn(
                      "text-[11px] truncate",
                      st.status === "completed" && "line-through text-muted-foreground",
                    )}
                  >
                    {st.title ?? "Subtarefa"}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
